import { defineStore } from "pinia";

import { getDocumentDetails } from "@/services/document.js";
import { openAPIFactory } from "@/services/open-api";
import { useAppStore } from "@/stores/app.ts";
import { CodeDescription, useFundamentalStore } from "@/stores/fundamental.ts";
import type {
  CodeVerfahrensschritt,
  CodeVerfahrensunterlagetyp,
  CodeVormerkung,
  DokumentAnzahlRest,
  DokumentRest,
  GetDokumentAnzahlKlassifizierung,
  GetDokumentUebersichtKlassifizierung,
  GetDokumentUebersichtSort,
  GetVertragsdokumenteSort,
  PageDokumentRest,
  ValidationReportAsyncRest,
  VertragsdokumentRest,
  VorlageRest,
  XplanManagerStatusMessageRest,
} from "@/types/open-api";

export interface ValidationReport {
  fileID: string;
  data: ValidationReportAsyncRest | undefined;
}

export interface folders {
  [folder: string]: folder;
}

export interface folder {
  loading: boolean | undefined;
  documentData: PageDokumentRest;
  numberData: DokumentAnzahlRest;
}

export interface folderStructureElement {
  name: string;
  ordnername?: string;
  codeVerfahrensschritt?: CodeVerfahrensschritt;
}

export interface contractData {
  loading: boolean | undefined;
  documentData: PageDokumentRest;
}

export interface DocumentsStoreState {
  documentTemplates: VorlageRest[];
  documentContainer: {
    [key: string]: {
      folders: folders;
      loading: boolean;
    };
  };
  contractContainer: {
    [key: string]: contractData;
  };
  documentFolderStructure: { [key: string]: folderStructureElement[] };
  planUploadError: boolean;
  planUploadErrorMessage: string;
  planUploadErrorTraceId: string;
  validationReports: ValidationReport[];
  documentTemplateCreation: {
    isInProgress: boolean;
    documentTemplateFile?: File;
    documentTemplateDescription?: string;
    documentTemplateCode?: string;
    documentType?: string;
  };
  documentTemplateAssignment: {
    codeVerfahrensschritt: CodeDescription;
    codeVerfahrenssteuerung: CodeDescription;
    codeVerfahrensteilschritt: CodeDescription;
  }[];
  generationModal: {
    open: boolean;
    steps: CodeDescription[];
  };
  documentRestoreData: {
    documentRestored: object;
    versionRestored: string | undefined;
    newVersion: string | undefined;
  };
}

export const useDocumentsStore = defineStore("documents", {
  state: (): DocumentsStoreState => ({
    documentTemplates: [],
    documentFolderStructure: {},
    documentContainer: {},
    contractContainer: {},
    planUploadError: false,
    planUploadErrorMessage: "",
    planUploadErrorTraceId: "",
    validationReports: [],
    documentTemplateCreation: {
      isInProgress: false,
      documentTemplateFile: undefined,
      documentTemplateDescription: undefined,
      documentTemplateCode: undefined,
      documentType: undefined,
    },
    documentTemplateAssignment: [],
    generationModal: {
      open: false,
      steps: [],
    },
    documentRestoreData: {
      documentRestored: {},
      versionRestored: undefined,
      newVersion: undefined,
    },
  }),
  actions: {
    /**
     * Loads all document templates and updates 'documentTemplates' state
     */
    loadDocumentTemplates(): Promise<VorlageRest | unknown[]> {
      const appStore = useAppStore();

      return new Promise((resolve, reject) => {
        openAPIFactory
          .vorlageResourceApiFactory()
          .getVorlageUebersicht()
          .then((response) => {
            this.documentTemplates = response.data.sort((a, b) => {
              return a.beschreibung && b.beschreibung && a.beschreibung > b.beschreibung ? 1 : -1;
            });

            resolve(response.data);
          })
          .catch((error) => {
            appStore.showErrorModal({
              response: error,
              customErrorMessage: "Laden der Mustervorlagen fehlgeschlagen!",
            });

            reject(error);
          });
      });
    },
    /**
     * Loads the number of files per folder of a given proceeding.
     */
    loadNumberOfDocuments(
      proceedingId: string,
      params?: {
        suchBegriff?: string;
        dateiNamen?: string[];
        bearbeiter?: string[];
        datum?: string[];
        codeDokumentstatus?: string[];
        versionen?: string[];
        codeVormerkung?: CodeVormerkung[];
        codeVerfahrensunterlagetyp?: CodeVerfahrensunterlagetyp[];
        fileID?: string[];
        klassifizierung?: GetDokumentAnzahlKlassifizierung[];
        ordnername?: string[];
        codeVerfahrensschritt?: CodeVerfahrensschritt[];
        papierkorb?: boolean;
      },
    ) {
      const appStore = useAppStore();

      if (typeof this.documentContainer[proceedingId] === "undefined") {
        this.documentContainer[proceedingId] = { loading: true, folders: {} };
      } else {
        this.documentContainer[proceedingId].loading = true;
      }

      const suchBegriff = params?.suchBegriff;
      const dateiNamen = params?.dateiNamen;
      const bearbeiter = params?.bearbeiter;
      const datum = params?.datum;
      const codeDokumentstatus = params?.codeDokumentstatus;
      const versionen = params?.versionen;
      const codeVormerkung = params?.codeVormerkung;
      const codeVerfahrensunterlagetyp = params?.codeVerfahrensunterlagetyp;
      const fileID = params?.fileID;
      const klassifizierung = params?.klassifizierung;
      const ordnername = params?.ordnername;
      const codeVerfahrensschritt = params?.codeVerfahrensschritt;
      const papierkorb = params?.papierkorb ?? false;

      return new Promise((resolve, reject) => {
        openAPIFactory
          .dokumentResourceApiFactory()
          .getDokumentAnzahl(
            proceedingId,
            suchBegriff,
            dateiNamen,
            bearbeiter,
            datum,
            codeDokumentstatus,
            versionen,
            codeVormerkung,
            codeVerfahrensunterlagetyp,
            fileID,
            klassifizierung,
            ordnername,
            undefined,
            codeVerfahrensschritt,
            papierkorb,
          )
          .then((response) => {
            this.setProceedingNumberOfDocuments(proceedingId, response?.data ?? []);
            resolve(response);
          })
          .catch((error) => {
            appStore.showErrorModal({
              response: error,
              customErrorMessage: "Das Laden der Dokumente ist fehlgeschlagen!",
            });
            reject(error);
          })
          .finally(() => {
            this.documentContainer[proceedingId].loading = false;
          });
      });
    },
    /**
     * Caches the number of documents per folder.
     */
    setProceedingNumberOfDocuments(proceedingId: string, numberOfDocuments: DokumentAnzahlRest[]) {
      const documentContainer = { ...this.documentContainer };
      let folderStructures = [
        { name: "Planwerke", ordnername: "Planwerke" },
        { name: "Übergreifende Dokumente" },
        { name: "Verträge", ordnername: "Verträge" },
      ];
      let folderStructuresFolders: folderStructureElement[] = [];
      let folderStructuresSteps: folderStructureElement[] = [];

      if (!documentContainer[proceedingId]) {
        documentContainer[proceedingId] = { folders: {}, loading: false };
      }

      numberOfDocuments.forEach((folder) => {
        const folderName =
          folder.verfahrensschritt?.code ?? folder.ordnername ?? "Übergreifende Dokumente";

        // filter out 9998 = kein VS
        if (folderName === "9998") {
          return;
        }

        // set folder structure
        if (
          folder.ordnername &&
          folder.ordnername !== "Planwerke" &&
          folder.ordnername !== "Verträge"
        ) {
          folderStructuresFolders.push({ name: folder.ordnername, ordnername: folder.ordnername });
        } else if (folder.verfahrensschritt?.name) {
          folderStructuresSteps.push({
            name: folder.verfahrensschritt.name,
            codeVerfahrensschritt: folder.verfahrensschritt,
          });
        }

        // set number of documents in documentContainer
        if (!folderName || !documentContainer[proceedingId]?.folders?.[folderName]?.numberData) {
          if (!documentContainer[proceedingId]?.folders) {
            documentContainer[proceedingId].folders = {};
          }
          if (!documentContainer[proceedingId].folders[folderName]) {
            documentContainer[proceedingId].folders[folderName] = {
              loading: undefined,
              documentData: {},
              numberData: {},
            };
          }
          documentContainer[proceedingId].folders[folderName].numberData = folder;
        } else {
          documentContainer[proceedingId].folders[folderName].numberData = {
            ...documentContainer[proceedingId].folders[folderName].numberData,
            ...folder,
          };
        }
      });

      // set anzahl to 0 for folders not found in numberOfDocuments
      Object.keys(documentContainer[proceedingId].folders).forEach((folderName) => {
        if (
          folderName !== "Übergreifende Dokumente" &&
          !numberOfDocuments.find(
            (f) => f.ordnername === folderName || f.verfahrensschritt?.code === folderName,
          )
        ) {
          documentContainer[proceedingId].folders[folderName].numberData.anzahl = 0;
        }
      });

      this.documentFolderStructure[proceedingId] = folderStructures.concat(
        folderStructuresFolders,
        folderStructuresSteps,
      );
      this.documentContainer = documentContainer;
    },
    /**
     * Caches the documents of a given folder.
     */
    setProceedingDocuments(proceedingId: string, folderName: string, documents: PageDokumentRest) {
      const documentContainer = { ...this.documentContainer };

      if (!documentContainer[proceedingId]) {
        documentContainer[proceedingId] = { loading: false, folders: {} };
      }
      if (!documentContainer[proceedingId].folders[folderName]) {
        documentContainer[proceedingId].folders[folderName] = {
          loading: false,
          documentData: {},
          numberData: {},
        };
      }

      documentContainer[proceedingId].folders[folderName].documentData = documents;

      this.documentContainer = documentContainer;
    },
    /**
     * Sets the loading status of the given document folder of a proceeding.
     */
    setDocumentFolderLoadingStatus(proceedingId: string, folder: string, loading: boolean) {
      if (!this.documentContainer[proceedingId]?.folders) {
        this.documentContainer[proceedingId].folders = {};
      }
      if (this.documentContainer[proceedingId]?.folders?.[folder]) {
        this.documentContainer[proceedingId].folders[folder].loading = loading;
      } else {
        this.documentContainer[proceedingId].folders[folder] = {
          loading: loading,
          documentData: {},
          numberData: {},
        };
      }
    },
    /**
     * Loads the contracts of a given proceeding.
     * If no ID is given, the ID is derived from the path. If the ID is provided encapsulated
     * within an object, a forced reload is done.
     */
    async loadProceedingContracts(payload: {
      proceedingID?: string | { planID: string } | undefined;
      params?: {
        suchBegriff?: string;
        dateiNamen?: string[];
        bearbeiter?: string[];
        datum?: string[];
        codeDokumentstatus?: string[];
        versionen?: string[];
        codeVormerkung?: CodeVormerkung[];
        codeVerfahrensunterlagetyp?: CodeVerfahrensunterlagetyp[];
        fileID?: string[];
        klassifizierung?: GetDokumentUebersichtKlassifizierung[];
        codeVerfahrensschritt?: CodeVerfahrensschritt;
        papierkorb?: boolean;
        mitErgaenzungen: boolean;
        page?: number;
        size?: number;
        sort?: GetVertragsdokumenteSort[];
      };
    }): Promise<VertragsdokumentRest[] | false> {
      const appStore = useAppStore();
      const fundamentalStore = useFundamentalStore();
      const documentStatusCodesNotDeleted = fundamentalStore
        .codelistByName("Dokumentstatus")
        .map((status) => status.code)
        .filter((code): code is string => code !== undefined && code !== "1800");
      let pID: string | { planID: string } | undefined;

      pID =
        typeof payload.proceedingID === "string"
          ? payload.proceedingID
          : payload.proceedingID?.planID;

      if (pID !== undefined) {
        const planID = pID;

        if (!this.contractContainer[planID]) {
          this.contractContainer[planID] = { loading: undefined, documentData: {} };
        }
        this.contractContainer[planID].loading = true;

        return new Promise((resolve, reject) => {
          const suchBegriff = payload.params?.suchBegriff;
          const dateiNamen = payload.params?.dateiNamen || [];
          const bearbeiter = payload.params?.bearbeiter || [];
          const datum = payload.params?.datum || [];
          const codeDokumentstatus = payload.params?.papierkorb
            ? ["1800"]
            : payload.params?.codeDokumentstatus
              ? payload.params?.codeDokumentstatus
              : documentStatusCodesNotDeleted;
          const versionen = payload.params?.versionen || [];
          const codeVormerkung = payload.params?.codeVormerkung || [];
          const codeVerfahrensunterlagetyp = payload.params?.codeVerfahrensunterlagetyp || [];
          const fileID = payload.params?.fileID || [];
          const klassifizierung = payload.params?.klassifizierung || [];
          const codeVerfahrensschritt = payload.params?.codeVerfahrensschritt
            ? [payload.params?.codeVerfahrensschritt]
            : [];
          const papierkorb = payload.params?.papierkorb || false;
          const mitErgaenzungen = payload.params?.mitErgaenzungen || false;
          const page = payload.params?.page || 0;
          const size = payload.params?.size || 25;
          const sort = payload.params?.sort;

          openAPIFactory
            .vertragsdokumentResourceApiFactory()
            .getVertragsdokumente(
              planID,
              suchBegriff,
              dateiNamen,
              bearbeiter,
              datum,
              codeDokumentstatus,
              versionen,
              codeVormerkung,
              codeVerfahrensunterlagetyp,
              fileID,
              klassifizierung,
              codeVerfahrensschritt,
              papierkorb,
              mitErgaenzungen,
              page,
              size,
              sort,
            )
            .then((response) => {
              this.contractContainer[planID].documentData = response.data;
              resolve(response?.data?.content ?? false);
            })
            .catch((error) => {
              appStore.showErrorModal({
                response: error,
                customErrorMessage: "Fehler beim Laden der Verträge!",
              });

              reject(error);
            })
            .finally(() => {
              this.contractContainer[planID].loading = false;
            });
        });
      }

      return Promise.resolve(false);
    },
    /**
     * Loads the contents of the document library of a given proceeding.
     * If no ID is given, the ID is derived from the path. If the ID is provided encapsulated
     * within an object, a forced reload is done.
     * If xplan true returns xplanDocumentContainer else returns documentContainer.
     */
    async loadProceedingDocuments(payload: {
      proceedingID?: string | { planID: string } | undefined;
      params?: {
        suchBegriff?: string;
        dateiNamen?: string[];
        bearbeiter?: string[];
        datum?: string[];
        codeDokumentstatus?: string[];
        versionen?: string[];
        codeVormerkung?: CodeVormerkung[];
        codeVerfahrensunterlagetyp?: CodeVerfahrensunterlagetyp[];
        fileID?: string[];
        klassifizierung?: GetDokumentUebersichtKlassifizierung[];
        ordnername?: string | string[];
        codeVerfahrensschritt?: string;
        papierkorb?: boolean;
        page?: number;
        size?: number;
        sort?: GetDokumentUebersichtSort[];
      };
    }): Promise<DokumentRest[] | false> {
      const appStore = useAppStore();
      const fundamentalStore = useFundamentalStore();
      const documentStatusCodesNotDeleted = fundamentalStore
        .codelistByName("Dokumentstatus")
        .map((status) => status.code)
        .filter((code): code is string => code !== undefined && code !== "1800");

      let folder = payload.params?.ordnername ?? payload.params?.codeVerfahrensschritt ?? undefined;

      // folder gets set to codeVerfahrensschritt if ordnername is 'null' and
      // codeVerfahrensschritt is not '9998' which is the code for 'Übergreifende Dokumente'
      if (
        folder === "null" &&
        payload.params?.codeVerfahrensschritt &&
        payload.params?.codeVerfahrensschritt !== "9998"
      ) {
        folder = payload.params?.codeVerfahrensschritt;
      } else if (!folder || folder === "9998" || folder === "null" || Array.isArray(folder)) {
        folder = "Übergreifende Dokumente";
      }

      let pID =
        typeof payload.proceedingID === "string"
          ? payload.proceedingID
          : payload.proceedingID?.planID;

      if (pID !== undefined) {
        const planID = pID;

        if (typeof this.documentContainer[planID] === "undefined") {
          this.documentContainer[planID] = { loading: false, folders: {} };
        }

        this.setDocumentFolderLoadingStatus(planID, folder, true);

        return new Promise((resolve, reject) => {
          const suchBegriff = payload.params?.suchBegriff;
          const dateiNamen = payload.params?.dateiNamen || [];
          const bearbeiter = payload.params?.bearbeiter || [];
          const datum = payload.params?.datum || [];
          const codeDokumentstatus = payload.params?.papierkorb
            ? ["1800"]
            : payload.params?.codeDokumentstatus
              ? payload.params?.codeDokumentstatus
              : documentStatusCodesNotDeleted;
          const versionen = payload.params?.versionen || [];
          const codeVormerkung = payload.params?.codeVormerkung || [];
          const codeVerfahrensunterlagetyp = payload.params?.codeVerfahrensunterlagetyp || [];
          const fileID = payload.params?.fileID || [];
          const klassifizierung = payload.params?.klassifizierung || [];
          const ordnername =
            payload.params?.ordnername !== undefined
              ? Array.isArray(payload.params.ordnername)
                ? payload.params.ordnername
                : [payload.params.ordnername]
              : [];
          const codeVerfahrensschritt = payload.params?.codeVerfahrensschritt
            ? [payload.params?.codeVerfahrensschritt]
            : [];
          const papierkorb = payload.params?.papierkorb || false;
          const page = payload.params?.page || 0;
          const size = payload.params?.size || 25;
          const sort = payload.params?.sort;

          openAPIFactory
            .dokumentResourceApiFactory()
            .getDokumentUebersicht(
              planID,
              suchBegriff,
              dateiNamen,
              bearbeiter,
              datum,
              codeDokumentstatus,
              versionen,
              codeVormerkung,
              codeVerfahrensunterlagetyp,
              fileID,
              klassifizierung,
              ordnername,
              undefined,
              codeVerfahrensschritt as undefined | CodeVerfahrensschritt[],
              papierkorb,
              page,
              size,
              sort,
            )
            .then((response) => {
              this.setProceedingDocuments(planID, folder, response.data);
              resolve(response?.data?.content ?? false);
            })
            .catch((error) => {
              appStore.showErrorModal({
                response: error,
                customErrorMessage:
                  "Fehler bei der Ermittlung des Inhaltes der Dokumentenbibliothek!",
              });

              reject(error);
            })
            .finally(() => {
              this.setDocumentFolderLoadingStatus(planID, folder, false);
            });
        });
      }

      return Promise.resolve(false);
    },
    /**
     * Reload document data for fileID and update store
     * @param payload
     * @param payload.planID The identifier of the proceeding.
     * @param payload.fileID The identifier of the document.
     */
    reloadDocumentByFileID(payload: { planID: string; fileID: string }): Promise<DokumentRest> {
      return new Promise((resolve, reject) => {
        this.documentContainer[payload.planID].loading = true;

        getDocumentDetails(payload.fileID, payload.planID)
          .then((document) => {
            this.setDocumentData(document, payload.planID);
            resolve(document);
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            this.documentContainer[payload.planID].loading = false;
          });
      });
    },
    /**
     * Updates a document status.
     * @param payload
     * @param payload.planID The identifier of the proceeding.
     * @param payload.fileID The identifier of the document.
     * @param payload.showPageLoadingIndicator Should a page loading indicator be displayed?
     * @param payload.newStatus The new status of the document.
     * @param payload.callbackSuccess Potential callback function after successful file generation.
     * @param payload.callbackError Potential callback function in case of an error.
     */
    setDocumentStatus(payload: {
      planID: string;
      fileID: string;
      showPageLoadingIndicator: boolean;
      newStatus: string;
      callbackSuccess: (arg: DokumentRest) => void;
      callbackError: (arg: unknown) => void;
    }): Promise<DokumentRest | false> {
      if (payload.planID !== undefined) {
        const appStore = useAppStore();

        if (payload.showPageLoadingIndicator) {
          appStore.showPageLoadingIndicator({
            id: "documentStatusUpdateInProgress",
            text: "Einen Moment bitte, der Dokumentstatus wird geändert.",
          });
        }

        return new Promise((resolve, reject) => {
          openAPIFactory
            .dokumentResourceApiFactory()
            .setzeDokumentStatus(payload.planID, payload.fileID, payload.newStatus)
            .then((response) => {
              if (payload.callbackSuccess) {
                payload.callbackSuccess(response.data);
              }
              this.setDocumentData(response.data, payload.planID);
              resolve(response.data);
            })
            .catch((error) => {
              if (payload.callbackError && error) {
                payload.callbackError(error);
              } else {
                appStore.showErrorModal({
                  response: error,
                  customErrorMessage: "Das Ändern des Dokumentstatus ist fehlgeschlagen!",
                });
              }

              reject(error);
            })
            .finally(() => {
              if (payload.showPageLoadingIndicator) {
                appStore.hidePageLoadingIndicator("documentStatusUpdateInProgress");
              }
            });
        });
      }

      return Promise.resolve(false);
    },
    /**
     * Uploads a XPlanGML-Archive.
     * @param planID The identifier of the proceeding.
     * @param file The file to upload.
     */
    uploadXPlanGMLArchive(planID: string, file: File): Promise<boolean> {
      const appStore = useAppStore();

      if (planID !== undefined) {
        appStore.showPageLoadingIndicator({
          id: "uploadXPlanGMLArchive",
          text: "Einen Moment bitte, die Datei wird hochgeladen.",
        });

        return new Promise((resolve, reject) => {
          openAPIFactory
            .xplanManagerResourceApiFactory()
            .uploadXPlanGml(file, planID, undefined, true, false)
            .then(async () => {
              this.resetPlanUploadError();

              await this.loadProceedingDocuments({
                proceedingID: planID,
                params: {
                  codeVerfahrensunterlagetyp: [{ code: "0350" }],
                  ordnername: "Planwerke",
                  size: 2000,
                },
              });

              resolve(true);
            })
            .catch((error) => {
              const defaultErrorMsg =
                "Es ist ein unerwarteter Fehler beim Hochladen des Planwerks aufgetreten.";

              this.planUploadError = true;
              this.planUploadErrorMessage =
                error?.response?.data.message || error?.message || defaultErrorMsg;
              this.planUploadErrorTraceId = error?.response?.headers?.traceid;

              reject();
            })
            .finally(() => {
              appStore.hidePageLoadingIndicator("uploadXPlanGMLArchive");
            });
        });
      }

      return Promise.resolve(false);
    },
    /**
     * Loads the validation report.
     * @param planID
     * @param fileID
     * @param uuID
     **/
    loadValidationReport(
      planID: string,
      fileID: string,
      uuID: string,
    ): Promise<ValidationReportAsyncRest | false> {
      if (planID !== undefined) {
        return new Promise((resolve, reject) => {
          openAPIFactory
            .xplanManagerResourceApiFactory()
            .getValidationReport(planID, fileID, uuID)
            .then(async (response) => {
              this.setValidationReport({
                fileID: fileID,
                data: Object.keys(response.data).length ? response.data : undefined,
              });

              resolve(response.data);
            })
            .catch((error) => {
              this.setValidationReport({
                fileID: fileID,
                data: undefined,
              });

              reject(error);
            });
        });
      }

      return Promise.resolve(false);
    },
    /**
     * Deletes a published XplanGML.
     * @param {string} planID
     * @param {string} fileID
     */
    deleteXPlanGML(planID: string, fileID: string): Promise<XplanManagerStatusMessageRest | false> {
      const appStore = useAppStore();

      if (planID !== undefined) {
        return new Promise((resolve, reject) => {
          openAPIFactory
            .xplanManagerResourceApiFactory()
            .deleteXplanGml(planID, fileID)
            .then(async (response) => {
              await this.loadProceedingDocuments({
                proceedingID: planID,
                params: {
                  codeVerfahrensunterlagetyp: [{ code: "0350" }],
                  ordnername: "Planwerke",
                  size: 2000,
                },
              });

              resolve(response.data);
            })
            .catch((error) => {
              appStore.showErrorModal({
                response: error,
                customErrorMessage: "Das Löschen des XplanGML ist fehlgeschlagen!",
              });

              reject(error);
            });
        });
      }

      return Promise.resolve(false);
    },
    /**
     * Resets uploadErrorMessage and error state for xplan upload.
     */
    resetPlanUploadError() {
      this.planUploadError = false;
      this.planUploadErrorMessage = "";
      this.planUploadErrorTraceId = "";
    },
    /**
     * Sets report after validating XPlanGml archive.
     * @param payload Validation report object.
     *
     */
    setValidationReport(payload: ValidationReport) {
      if (!payload.data) return;

      const validationReports = [...this.validationReports];
      const index = validationReports.findIndex((report) => report.fileID === payload.fileID);

      if (index !== -1) {
        validationReports[index] = {
          fileID: payload.fileID,
          data: payload.data,
        };
      } else {
        validationReports.push({
          fileID: payload.fileID,
          data: payload.data,
        });
      }

      this.validationReports = validationReports;
    },
    /**
     * Sets inputs from modal 1 of document template creation (for the zurück functionality).
     */
    setDocumentTemplateModal1Data(payload: {
      isInProgress: boolean;
      documentTemplateFile: File;
      documentTemplateDescription: string;
      documentTemplateCode: string;
      documentType: string;
    }) {
      this.documentTemplateCreation = {
        ...this.documentTemplateCreation,
        isInProgress: payload.isInProgress,
        documentTemplateFile: payload.documentTemplateFile,
        documentTemplateDescription: payload.documentTemplateDescription,
        documentTemplateCode: payload.documentTemplateCode,
        documentType: payload.documentType,
      };
    },
    /**
     * Resets document template creation data
     */
    resetDocumentTemplateData() {
      this.documentTemplateCreation = {
        isInProgress: false,
        documentTemplateFile: undefined,
        documentTemplateDescription: undefined,
        documentTemplateCode: undefined,
        documentType: undefined,
      };

      this.documentTemplateAssignment = [];
    },
    openDocumentGenerationModal(steps = []) {
      this.generationModal.open = true;
      this.generationModal.steps = steps;
    },
    closeDocumentGenerationModal() {
      this.generationModal.open = false;
      this.generationModal.steps = [];
    },
    /*Update a particular Document data by ID */
    setDocumentData(documentData: DokumentRest, planID: string, folder?: string) {
      let currentFolder = folder;
      const isContract = documentData.ordnername === "Verträge";

      // if no folder is provided, get the folder by searching for the fileID in all folders
      if (!currentFolder && !isContract) {
        for (const [key, value] of Object.entries(this.documentContainer[planID]?.folders)) {
          if (
            value.documentData?.content?.find(
              (documentItem) => documentItem.fileID === documentData.fileID,
            )
          ) {
            currentFolder = key;
            break;
          }
        }
      }

      let itemIndex;

      // find the index of the file in the documents array of the folder
      if (currentFolder) {
        const documentContent = isContract
          ? this.contractContainer[planID]?.documentData?.content
          : this.documentContainer[planID]?.folders?.[currentFolder]?.documentData?.content;

        itemIndex = documentContent?.findIndex(
          (documentItem) => documentItem.fileID === documentData.fileID,
        );

        // if the document exists, replace it with the provided data
        if (itemIndex !== undefined && itemIndex !== -1 && documentContent?.[itemIndex]) {
          documentContent?.splice(itemIndex, 1, { ...documentData });
        }
      }
    },
    /**
       save data of the document for which the version is restored, in state to avoid too much prop drilling
    */
    setDocumentForVersionRestore(
      documentData: object,
      versionRestored: string | undefined,
      newVersion: string | undefined,
    ) {
      this.documentRestoreData = {
        documentRestored: { ...documentData },
        versionRestored,
        newVersion,
      };
    },
    resetDataForVersionRestore() {
      this.documentRestoreData = {
        documentRestored: {},
        versionRestored: undefined,
        newVersion: undefined,
      };
    },
  },
  getters: {
    /**
     * Get all documents of a specific proceeding ID
     */
    allDocumentsByProceedingID(): (proceedingID: string, folder?: string) => folders | folder {
      return (proceedingID, folder?) => {
        if (
          typeof this.documentContainer[proceedingID] !== "undefined" &&
          this.documentContainer[proceedingID].folders
        ) {
          return folder
            ? (this.documentContainer[proceedingID].folders[folder] ?? {})
            : this.documentContainer[proceedingID].folders;
        }

        return {};
      };
    },
    /**
     * Get Document by proceedingID and fileID
     */
    documentByFileID(): (proceedingID: string, fileID: string) => DokumentRest | undefined {
      return (proceedingID, fileID) => {
        //search in files
        for (const value of Object.values(this.documentContainer[proceedingID]?.folders)) {
          const foundDocument = value.documentData?.content?.find(
            (documentItem) => documentItem.fileID === fileID,
          );

          if (foundDocument) {
            return foundDocument;
          }
        }
        //if no file is found, search in contracts
        return this.contractsAndAdditionsByProceedingID(proceedingID)?.documentData?.content?.find(
          (contractItem) => contractItem.fileID === fileID,
        );
      };
    },
    /**
     * Get the contracts of a specific proceeding.
     */
    contractsByProceedingID(): (proceedingID: string) => VertragsdokumentRest[] {
      return (proceedingID) => {
        const contractData = this.contractContainer[proceedingID]?.documentData;
        const contracts = contractData?.content;

        if (contracts) {
          return contracts.filter(
            (contract) => contract["@class"] === "VertragsdokumentRest",
          ) as VertragsdokumentRest[];
        }

        return [];
      };
    },
    /**
     * Get the contracts and additions of a specific proceeding.
     * Returns the document folder including the pagination and loading data.
     */
    contractsAndAdditionsByProceedingID(): (proceedingID: string) => contractData {
      return (proceedingID) => {
        return this.contractContainer[proceedingID] ?? {};
      };
    },
    /**
     * Get the xplanDocuments of a specific planID.
     */
    xplanDocumentsByPlanID(): (planID: string) => DokumentRest[] {
      return (planID) => {
        const documentData = this.allDocumentsByProceedingID(planID, "Planwerke")
          ?.documentData as PageDokumentRest;

        return documentData?.content ?? [];
      };
    },
    /**
     * Get the validation results of a specific file.
     */
    validationReportsByFileID(): (fileID: string) => ValidationReportAsyncRest | undefined {
      return (fileID) => {
        const foundItem: ValidationReport | undefined = this.validationReports.find(
          (item) => item.fileID === fileID,
        );

        return foundItem?.data;
      };
    },
    /**
     * Get the WmsUrl of a specific file.
     */
    wmsUrlByFileID(): (fileID: string) => string | undefined {
      return (fileID) => {
        const foundItem: ValidationReport | undefined = this.validationReports.find(
          (item) => item.fileID === fileID,
        );

        return foundItem?.data?.wmsUrl;
      };
    },
  },
});
